// src/components/AudienceCard.jsx
import React, { useState } from "react"
import {
  Card,
  CardBody,
  CardTitle,
  Col,
  Row,
  Pagination,
  PaginationItem,
  PaginationLink,
  Spinner,
} from "reactstrap"
import { Link, useNavigate } from "react-router-dom"
import SnackbarComponent from "components/SnackBar"
import { useSelector } from "react-redux"
import PrimaryButton from "newcomponents/primary-button"
import AddIcon from "@mui/icons-material/Add"

const BLUE = "#2990DF"

const AudienceCard = ({ audiences = [], loading }) => {
  const mode = useSelector(s => s.Layout?.layoutMode)
  const navigate = useNavigate()
  const userData = useSelector(state => state.Login.userData)

  const [currentPage, setCurrentPage] = useState(1)
  const audiencesPerPage = 5

  const validAudiences = Array.isArray(audiences) ? audiences : []
  const indexOfLastAudience = currentPage * audiencesPerPage
  const indexOfFirstAudience = indexOfLastAudience - audiencesPerPage
  const currentAudiences = validAudiences.slice(
    indexOfFirstAudience,
    indexOfLastAudience,
  )
  const totalPages = Math.max(
    1,
    Math.ceil(validAudiences.length / audiencesPerPage),
  )

  const paginate = pageNumber => {
    if (pageNumber > 0 && pageNumber <= totalPages) setCurrentPage(pageNumber)
  }

  return (
    <>
      {/* active-page blue override (why: bootstrap default is gray) */}
      <style>{`
        .pagination .page-item.active .page-link {
          background-color: ${BLUE};
          border-color: ${BLUE};
          color: #fff;
        }
        .pagination .page-link:focus {
          box-shadow: 0 0 0 0.25rem rgba(41,144,223,0.25);
        }
      `}</style>

      <SnackbarComponent />
      <Card
        className="br"
        style={{
          borderRadius: 8,
          height: "100%",
          backgroundColor: mode === "dark" ? "#252F40" : "#fff",
        }}
      >
        <CardBody
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%", // ensures pagination can stick to bottom
          }}
        >
          <Row className="align-items-center mb-3">
            <Col xl={6} lg={6} md={6} className="d-flex align-items-center">
              <CardTitle
                className="h2 mb-0 text-uppercase"
                style={{
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  color: mode === "dark" ? "white" : "#1F2937",
                }}
              >
                Audiences
              </CardTitle>
            </Col>
            <Col
              xl={6}
              lg={6}
              md={6}
              className="d-flex justify-content-end align-items-center"
            >
              {userData.role !== "viewer" && (
                <PrimaryButton
                  style={{
                    padding: "10px 20px",
                    fontSize: "16px",
                    fontWeight: "400",
                    borderRadius: "8px",
                  }}
                  icon={<AddIcon />}
                  onClick={() => navigate("/dashboard/create-audience")}
                >
                  Create New
                </PrimaryButton>
              )}
            </Col>
          </Row>

          <div
            style={{
              height: "0.1px",
              width: "100%",
              background: "gray",
            }}
          />

          {/* Audience List */}
          <div
            className="inbox-wid table-responsive"
            style={{
              minHeight: "10vh",
              flex: 1, // expands so pagination sits at the bottom
            }}
          >
            {loading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "200px",
                }}
              >
                <Spinner
                  color="primary"
                  style={{ height: "3rem", width: "3rem" }}
                />
              </div>
            ) : currentAudiences.length > 0 ? (
              currentAudiences.map((audience, key) => (
                <Link key={key} to="#" className="text-dark">
                  <div className="inbox-item">
                    <h6 className="inbox-item-author mb-1 font-size-14">
                      {audience.name}
                    </h6>
                    <p
                      className="inbox-item-text text-muted mb-0"
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "200px",
                      }}
                    >
                      {audience.description}
                    </p>
                  </div>
                </Link>
              ))
            ) : (
              <p>No audiences available.</p>
            )}
          </div>

          {/* Pagination pinned to bottom */}
          <Pagination
            className="mt-3 justify-content-center"
            style={{ marginTop: "auto" }} // pushes it to the bottom
          >
            <PaginationItem disabled={currentPage === 1}>
              <PaginationLink
                previous
                onClick={() => paginate(currentPage - 1)}
              />
            </PaginationItem>

            {Array.from({ length: totalPages }).map((_, i) => (
              <PaginationItem active={i + 1 === currentPage} key={i}>
                <PaginationLink onClick={() => paginate(i + 1)}>
                  {i + 1}
                </PaginationLink>
              </PaginationItem>
            ))}

            <PaginationItem disabled={currentPage === totalPages}>
              <PaginationLink next onClick={() => paginate(currentPage + 1)} />
            </PaginationItem>
          </Pagination>
        </CardBody>
      </Card>
    </>
  )
}

export default AudienceCard
